/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OTPInput from 'react-otp-input';

import {
  ButtonContainer,
  ScreenContent,
  MainContainer,
  Title,
  HeaderWrapper,
  Form,
  SubmitButton,
  BounceDiv,
  Resend,
  Text,
  TextLight,
  ResendContainer,
} from './VerificationEmailPage.styles';

import Footer from '../../../components/Footer/Footer';
import Header from '../../../components/Header/Header';
import Loader from '../../../components/Loader/Loader';

import { ReactComponent as Loading } from '../../../assets/icons/loader-white.svg';
import openToast from '../../../utils/toast';

import { selectedLocale } from '../../../store/slices/common/slice';
import { sendVerificationCodeEmailAgain } from '../../../store/slices/auth/asyncThunks';
import { verifyEmail } from '../../../store/slices/user/asyncThunks';
import { selectUserInfo, selectVerifyEmailPending } from '../../../store/slices/user/slice';

const VerificationEmailPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toastId = useRef(null);

  const locale = useSelector(selectedLocale);
  const loading = useSelector(selectVerifyEmailPending);

  const { email } = useSelector(selectUserInfo);

  const [otp, setOtp] = useState('');

  const ar = locale === 'ar';

  const [timeRemaining, setTimeRemaining] = useState(-1);

  const resendCode = async () => {
    try {
      setTimeRemaining(59);
      dispatch(sendVerificationCodeEmailAgain({ email }))
        .unwrap()
        .then(() => {
          openToast(toastId, 'resend-code', t('verification-email'));
        })
        .catch(err => {
          openToast(toastId, 'resend-code', 'error', t(err.message));
        });
    } catch (err) {
      setTimeRemaining(-1);
      openToast(toastId, 'resend-code', 'error', t(err.message));
    }
  };

  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [timeRemaining]);

  const handleSubmit = () => {
    if (otp.length === 4) {
      dispatch(verifyEmail({ code: otp }))
        .unwrap()
        .then(() => {
          openToast(toastId, 'verify-code-success', 'success', t('successfully-verified-account'));
          navigate('/dashboard');
        })
        .catch(error => {
          openToast(toastId, 'verify-code', 'error', t(error.message));
        });
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  useEffect(() => {
    document.addEventListener('keypress', handleKeyPress);
    window.scrollTo(0, 0);

    return () => {
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, [handleKeyPress]);

  return (
    <>
      <MainContainer>
        <HeaderWrapper>
          <Header />
        </HeaderWrapper>

        <BounceDiv>
          <ScreenContent>
            <Title>{t('welcome-to-ygii')}</Title>

            <Form>
              <OTPInput
                containerStyle={{
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                }}
                inputStyle={{
                  width: 55,
                  userSelect: 'none',
                  height: 55,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 12,
                  fontSize: 18,
                  fontFamily: 'Avenir-Medium',
                  letterSpacing: -0.2,
                  color: '#000',
                  borderRadius: 27.5,
                  border: '1px solid #7F7F98',
                  outline: 'none',
                  backgroundColor: '#fff',
                  boxShadow: '0px 1px 5px 1px #48488820',
                }}
                shouldAutoFocus
                value={otp}
                placeholder={'----'}
                onChange={setOtp}
                numInputs={4}
                renderSeparator={<span style={{ userSelect: 'none', color: 'transparent' }}>-</span>}
                renderInput={(props, i) => <input {...props} />}
              />
              <ResendContainer>
                <Text>{t('did-not-receive-the-code')}</Text>
                {timeRemaining <= 0 && <Resend onClick={resendCode}>{t('resend')}</Resend>}
                {timeRemaining > 0 && <TextLight>00:{timeRemaining}</TextLight>}
              </ResendContainer>
            </Form>
            {!loading ? (
              <ButtonContainer>
                <SubmitButton onClick={handleSubmit} disabled={otp.length < 4}>
                  {t('verify-your-account')}
                </SubmitButton>
              </ButtonContainer>
            ) : (
              <ButtonContainer>
                <SubmitButton disabled>
                  <Loading height={30} />
                </SubmitButton>
              </ButtonContainer>
            )}
          </ScreenContent>
        </BounceDiv>
      </MainContainer>
      {loading && <Loader />}
      <Footer ar={ar} />
    </>
  );
};

export default VerificationEmailPage;
